import { useEffect, useMemo, useState } from 'react';
import { searchTags, TagSort } from '../services/tags.service';
import { TagDto } from '../types/admin.types';
import {
    buildSearchInput,
    normalizeTagNameKey,
    parseSearchInput,
    stripTagsFromInput,
    toggleTagInInput
} from '../utils/video-search';

interface UseVideoSearchInputOptions {
    initialQuery?: string;
    initialTags?: string[];
    tagSearchDebounceMs?: number;
    tagLimit?: number;
    tagSort?: TagSort;
}

interface UseVideoSearchInputResult {
    inputValue: string;
    setInputValue: React.Dispatch<React.SetStateAction<string>>;
    parsedInput: ReturnType<typeof parseSearchInput>;
    parsedTagKey: string;
    selectedTags: TagDto[];
    displayTags: TagDto[];
    selectedTagKeys: Set<string>;
    showTagSkeletons: boolean;
    tagLoadFailed: boolean;
    toggleTag: (tagName: string) => void;
    clearTags: () => void;
}

const DEFAULT_TAG_LIMIT = 10;
const DEFAULT_TAG_DEBOUNCE_MS = 200;

export const useVideoSearchInput = (options: UseVideoSearchInputOptions = {}): UseVideoSearchInputResult => {
    const {
        initialQuery = '',
        initialTags = [],
        tagSearchDebounceMs = DEFAULT_TAG_DEBOUNCE_MS,
        tagLimit = DEFAULT_TAG_LIMIT,
        tagSort = 'recent'
    } = options;

    const [inputValue, setInputValue] = useState(() => buildSearchInput(initialQuery, initialTags));
    const [suggestedTags, setSuggestedTags] = useState<TagDto[]>([]);
    const [isLoadingTags, setIsLoadingTags] = useState(true);
    const [tagLoadFailed, setTagLoadFailed] = useState(false);

    const parsedInput = useMemo(() => parseSearchInput(inputValue), [inputValue]);
    const parsedTagKey = useMemo(() => parsedInput.tags.join('|'), [parsedInput.tags]);
    const activeTagQuery = parsedInput.activeTagQuery;

    const selectedTags = useMemo(() => {
        const suggestedMap = new Map(
            suggestedTags.map((tag) => [normalizeTagNameKey(tag.name), tag])
        );

        return parsedInput.tags.map((tagName) => {
            const key = normalizeTagNameKey(tagName);
            const existing = key ? suggestedMap.get(key) : undefined;
            if (existing) {
                return existing;
            }

            return {
                id: `custom-${key || tagName}`,
                name: tagName,
                usageCount: 0,
                lastUsedDate: null
            };
        });
    }, [parsedInput.tags, suggestedTags]);

    const selectedTagKeys = useMemo(
        () => new Set(selectedTags.map((tag) => normalizeTagNameKey(tag.name))),
        [selectedTags]
    );

    useEffect(() => {
        let isActive = true;
        const query = activeTagQuery ?? null;
        const handler = setTimeout(() => {
            const loadTags = async () => {
                setIsLoadingTags(true);
                setTagLoadFailed(false);
                try {
                    const tags = await searchTags(query, tagLimit, tagSort);
                    if (isActive) {
                        setSuggestedTags(tags);
                    }
                } catch (error) {
                    if (isActive) {
                        setSuggestedTags([]);
                        setTagLoadFailed(true);
                    }
                } finally {
                    if (isActive) {
                        setIsLoadingTags(false);
                    }
                }
            };

            loadTags();
        }, tagSearchDebounceMs);

        return () => {
            isActive = false;
            clearTimeout(handler);
        };
    }, [activeTagQuery, tagLimit, tagSearchDebounceMs, tagSort]);

    const filteredSuggestedTags = useMemo(() => {
        if (activeTagQuery === null) {
            return suggestedTags;
        }
        const query = activeTagQuery.toLowerCase();
        if (query.length === 0) {
            return suggestedTags;
        }
        return suggestedTags.filter((tag) => tag.name.toLowerCase().includes(query));
    }, [activeTagQuery, suggestedTags]);

    const displayTags = useMemo(() => {
        const combined = [...selectedTags];
        filteredSuggestedTags.forEach((tag) => {
            const key = normalizeTagNameKey(tag.name);
            if (key && !selectedTagKeys.has(key)) {
                combined.push(tag);
            }
        });
        return combined;
    }, [filteredSuggestedTags, selectedTags, selectedTagKeys]);

    const toggleTag = (tagName: string) => {
        setInputValue((prevValue) => toggleTagInInput(prevValue, tagName));
    };

    const clearTags = () => {
        setInputValue((prevValue) => stripTagsFromInput(prevValue));
    };

    return {
        inputValue,
        setInputValue,
        parsedInput,
        parsedTagKey,
        selectedTags,
        displayTags,
        selectedTagKeys,
        showTagSkeletons: isLoadingTags || tagLoadFailed,
        tagLoadFailed,
        toggleTag,
        clearTags
    };
};
